import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type {
  ImportedBrepRef,
  ImportedBrepRefSourceLevel,
  ImportedTopologyFaceTriangleRangeBinding,
  ImportedTopologyRef,
  LoaderMetadata,
} from './types'
import { asRecord } from './utils'
import { readImportedTopologyFaceTriangleRangeBindings } from './imported-topology-face-range-binding'

/**
 * 单个 primitive 的权威 BRep 绑定计划。
 *
 * 设计说明：
 * 1. 该结构严格对应 GLB 原始 JSON 中一个 primitive 的权威绑定结果；
 * 2. `sourceLevel` 用于调试和测试锁定优先级，不参与运行时语义；
 * 3. `primitiveExtras` 直接来自 `meshes[].primitives[].extras`，避免后续再从 three.js 对象上猜。
 */
export interface ImportedPrimitiveBrepBindingPlan extends ImportedBrepBodyOrdinalDescriptor {
  primitiveIndex: number
  entityId?: string
  bodyId?: string
  brepRef?: ImportedBrepRef
  topologyRef?: ImportedTopologyRef
  topologyFaceTriangleRanges?: ImportedTopologyFaceTriangleRangeBinding[]
  sourceLevel?: ImportedBrepRefSourceLevel
  primitiveExtras?: LoaderMetadata
}

/**
 * 单个 mesh 的权威 BRep 绑定计划。
 *
 * 设计说明：
 * 1. 一个 glTF mesh 可能对应多个 primitives，因此以 mesh 为单位持有 primitive 计划数组；
 * 2. `meshLevelBrepRef` 只描述协议里的 mesh 级引用，不代表最终一定按 mesh 粒度交互；
 * 3. 后续 part/assembly loader 会把这份 plan 投影为 body 级 topology source。
 */
export interface ImportedMeshBrepBindingPlan {
  meshLevelBrepRef?: ImportedBrepRef
  primitivePlans: ImportedPrimitiveBrepBindingPlan[]
}

/**
 * entity 层 BRep 绑定。
 */
interface ImportedEntityBrepBinding {
  /** 当前 entity 的稳定 ID。 */
  entityId?: string
  /** entity 显式声明的 body ID；旧导出可能直接复用 entityId。 */
  bodyId?: string
  /** entity 层 BRep 引用。 */
  brepRef?: ImportedBrepRef
  /** entity 层 topo sidecar 引用。 */
  topologyRef?: ImportedTopologyRef
}

/**
 * body 层 BRep 绑定。
 */
interface ImportedBodyBrepBinding extends ImportedBrepBodyOrdinalDescriptor {
  /** 当前 body 的稳定 ID。 */
  bodyId: string
  /** body 层 BRep 引用。 */
  brepRef?: ImportedBrepRef
  /** body 层 topo sidecar 引用。 */
  topologyRef?: ImportedTopologyRef
}

/**
 * body 绑定反查索引。
 */
interface ImportedBodyBrepBindingLookup {
  /** 按 primitive 下标命中的 body 绑定。 */
  byPrimitiveIndex: Map<number, ImportedBodyBrepBinding>
  /** 按 bodyId 命中的 body 绑定，用于兼容 entity 显式 bodyId 的历史结构。 */
  byBodyId: Map<string, ImportedBodyBrepBinding>
}

/**
 * meshBindings 中单条 primitive -> topo entry 绑定。
 */
interface ImportedMeshTopologyBinding {
  /** 当前绑定命中的 body ID。 */
  bodyId?: string
  /** 当前绑定显式声明的 topo entry key。 */
  topologyEntryKey?: string
  /** 当前绑定显式声明的 topo sidecar 引用。 */
  topologyRef?: ImportedTopologyRef
  /** 当前绑定显式声明的 GLB 可见面范围。 */
  topologyFaceTriangleRanges?: ImportedTopologyFaceTriangleRangeBinding[]
}

/**
 * 写回到 three.js 场景对象上的 BRep 绑定计划键。
 */
const IMPORTED_MESH_BREP_BINDING_PLAN_KEY = 'importedMeshBrepBindingPlan'

/**
 * 克隆元数据。
 *
 * @param value 待克隆值。
 * @returns 浅拷贝后的元数据。
 */
function cloneMetadata(value: unknown): LoaderMetadata | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }
  return { ...record }
}

/**
 * 归一化 BRep 引用。
 *
 * @param value 原始值。
 * @returns 合法的 BRep 引用；无效时返回 `undefined`。
 */
export function normalizeImportedBrepRef(value: unknown): ImportedBrepRef | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }

  const storage = typeof record.storage === 'string' ? record.storage.trim().toLowerCase() : 'sidecar'
  const uri = typeof record.uri === 'string' ? record.uri.trim() : ''
  const key = typeof record.key === 'string' ? record.key.trim() : ''
  const sha256 = typeof record.sha256 === 'string' ? record.sha256.trim() : ''
  const encoding = typeof record.encoding === 'string' ? record.encoding.trim() : ''
  const byteLength = typeof record.byteLength === 'number' && Number.isFinite(record.byteLength)
    ? record.byteLength
    : undefined

  if ((storage.length > 0 && storage !== 'sidecar') || !key || !sha256 || !encoding) {
    return undefined
  }

  return {
    uri,
    key,
    sha256,
    encoding,
    byteLength,
    storage: 'sidecar',
  }
}

/**
 * 归一化 topo sidecar 引用。
 *
 * 设计说明：
 * 1. topo sidecar 只能通过 GLB 中的显式 `topologyRef` 定位，不能再按 BRep 或 GLB 文件名推导；
 * 2. `sha256` 与 `byteLength` 允许后端暂时缺省，前端仍可按 `uri + key` 加载验证数据；
 * 3. `encoding` 只作为后端诊断元数据，不能作为前端是否读取的判断条件，否则后端字段改名会直接阻断最新 topo 快路径。
 *
 * @param value 原始 topologyRef 值。
 * @returns 合法的 topo sidecar 引用；无效时返回 `undefined`。
 */
export function normalizeImportedTopologyRef(value: unknown): ImportedTopologyRef | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }

  const storage = typeof record.storage === 'string' ? record.storage.trim().toLowerCase() : 'sidecar'
  const uri = typeof record.uri === 'string' ? record.uri.trim() : ''
  const key = typeof record.key === 'string' ? record.key.trim() : ''
  const sha256 = typeof record.sha256 === 'string' && record.sha256.trim().length > 0
    ? record.sha256.trim()
    : undefined
  const encoding = typeof record.encoding === 'string' && record.encoding.trim().length > 0
    ? record.encoding.trim()
    : undefined
  const byteLength = typeof record.byteLength === 'number' && Number.isFinite(record.byteLength)
    ? record.byteLength
    : undefined

  if ((storage.length > 0 && storage !== 'sidecar') || !uri || !key) {
    return undefined
  }

  return {
    uri,
    key,
    storage: 'sidecar',
    ...(sha256 ? { sha256 } : {}),
    ...(typeof byteLength === 'number' ? { byteLength } : {}),
    ...(encoding ? { encoding } : {}),
  }
}

/**
 * 用 meshBindings 中的 entry key 覆盖基础 topo 引用。
 *
 * @param baseRef body/entity/mesh 层级提供的 topo 引用。
 * @param topologyEntryKey meshBindings 中声明的 entry key。
 * @returns 指向 meshBindings entry 的 topo 引用。
 */
function overrideTopologyRefKey(
  baseRef: ImportedTopologyRef | undefined,
  topologyEntryKey: string | undefined,
): ImportedTopologyRef | undefined {
  if (!baseRef || !topologyEntryKey) {
    return baseRef
  }
  return {
    ...baseRef,
    key: topologyEntryKey,
    sha256: undefined,
    byteLength: undefined,
  }
}

/**
 * 读取可选字符串。
 *
 * @param value 原始字段值。
 * @returns 非空字符串。
 */
function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 读取 mesh 原始 extras。
 *
 * @param meshRecord mesh 原始记录。
 * @returns extras 记录。
 */
function resolveMeshExtrasRecord(meshRecord: Record<string, unknown>): Record<string, unknown> | null {
  return asRecord(meshRecord.extras)
}

/**
 * 读取 mesh 级 STEP 记录。
 *
 * 设计说明：
 * 1. 文档约定的正式位置是 `meshes[].extras.step`；
 * 2. 为了兼容历史导出，仍保留 `mesh.step / mesh.stepMetadata / mesh.assetStep` 的兜底；
 * 3. 但这些兼容字段只作为读取 STEP 附加信息的兜底，不得改变 BRep 引用的优先级。
 *
 * @param meshRecord mesh 原始记录。
 * @returns STEP 记录；不存在时返回 `null`。
 */
function resolveMeshStepRecord(meshRecord: Record<string, unknown>): Record<string, unknown> | null {
  const meshExtras = resolveMeshExtrasRecord(meshRecord)
  return (
    asRecord(meshExtras?.step)
    ?? asRecord(meshRecord.step)
    ?? asRecord(meshRecord.stepMetadata)
    ?? asRecord(meshRecord.assetStep)
    ?? asRecord(meshExtras?.stepMetadata)
    ?? asRecord(meshExtras?.assetStep)
    ?? null
  )
}

/**
 * 从 mesh 记录中读取 mesh 级 BRep 引用。
 *
 * 设计说明：
 * 1. 文档明确优先 `meshes[].extras.brep`；
 * 2. 历史兼容字段 `meshes[].brep` 只作为兜底；
 * 3. 这里不会读取 entity/body 引用，避免破坏协议优先级。
 *
 * @param meshRecord mesh 原始记录。
 * @returns mesh 级 BRep 引用。
 */
function resolveMeshLevelBrepRefFromMeshRecord(meshRecord: Record<string, unknown>): ImportedBrepRef | undefined {
  const meshExtras = resolveMeshExtrasRecord(meshRecord)
  return normalizeImportedBrepRef(meshExtras?.brep) ?? normalizeImportedBrepRef(meshRecord.brep)
}

/**
 * 从 mesh 记录中读取 mesh 级交互 topo 引用。
 *
 * @param meshRecord mesh 原始记录。
 * @returns mesh 级交互 topo 引用。
 */
function resolveMeshLevelTopologyRefFromMeshRecord(meshRecord: Record<string, unknown>): ImportedTopologyRef | undefined {
  const meshExtras = resolveMeshExtrasRecord(meshRecord)
  return normalizeImportedTopologyRef(meshExtras?.topologyRef)
    ?? normalizeImportedTopologyRef(meshExtras?.topology)
    ?? normalizeImportedTopologyRef(meshRecord.topologyRef)
    ?? normalizeImportedTopologyRef(meshRecord.topology)
}

/**
 * 读取 entity 级绑定表。
 *
 * @param stepRecord mesh 级 STEP 记录。
 * @returns primitiveIndex 到 entity 绑定的映射。
 */
function buildEntityBindingMap(
  stepRecord: Record<string, unknown> | null,
): Map<number, ImportedEntityBrepBinding> {
  const entityBindingMap = new Map<number, ImportedEntityBrepBinding>()
  const entityValues = Array.isArray(stepRecord?.entities) ? stepRecord.entities : []

  entityValues.forEach((entityValue) => {
    const entityRecord = asRecord(entityValue)
    if (!entityRecord) {
      return
    }

    const entityId = typeof entityRecord.entityId === 'string' && entityRecord.entityId.trim().length > 0
      ? entityRecord.entityId.trim()
      : undefined
    const explicitBodyId = typeof entityRecord.bodyId === 'string' && entityRecord.bodyId.trim().length > 0
      ? entityRecord.bodyId.trim()
      : undefined
    const bodyId = explicitBodyId ?? entityId
    const brepRef = normalizeImportedBrepRef(entityRecord.brepRef)
    const topologyRef = normalizeImportedTopologyRef(entityRecord.topologyRef)
    const primitiveIndices = Array.isArray(entityRecord.primitiveIndices)
      ? entityRecord.primitiveIndices.filter((value): value is number => Number.isInteger(value) && value >= 0)
      : []

    primitiveIndices.forEach((primitiveIndex) => {
      if (!entityBindingMap.has(primitiveIndex)) {
        entityBindingMap.set(primitiveIndex, {
          entityId,
          bodyId,
          brepRef,
          topologyRef,
        })
      }
    })
  })

  return entityBindingMap
}

/**
 * 读取 primitive 下标集合。
 *
 * @param value 原始 primitiveIndices 值。
 * @returns 合法 primitive 下标集合。
 */
function readPrimitiveIndices(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => Number.isInteger(item) && item >= 0)
    : []
}

/**
 * 读取 meshBindings 绑定到的 primitive 下标集合。
 *
 * @param bindingRecord meshBindings 原始记录。
 * @returns primitive 下标集合。
 */
function readMeshTopologyBindingPrimitiveIndices(bindingRecord: Record<string, unknown>): number[] {
  const primitiveIndex = typeof bindingRecord.primitiveIndex === 'number' && Number.isInteger(bindingRecord.primitiveIndex)
    && bindingRecord.primitiveIndex >= 0
    ? [bindingRecord.primitiveIndex]
    : []
  const primitiveIndices = readPrimitiveIndices(bindingRecord.primitiveIndices)
  return Array.from(new Set([...primitiveIndex, ...primitiveIndices]))
}

/**
 * 构建 GLB meshBindings 的 primitive 反查表。
 *
 * @param meshRecord mesh 原始记录。
 * @returns primitiveIndex 到 topo 绑定的映射。
 */
function buildMeshTopologyBindingMap(meshRecord: Record<string, unknown>): Map<number, ImportedMeshTopologyBinding> {
  const meshExtras = resolveMeshExtrasRecord(meshRecord)
  const meshBindings = Array.isArray(meshExtras?.meshBindings) ? meshExtras.meshBindings : []
  const bindingMap = new Map<number, ImportedMeshTopologyBinding>()

  meshBindings.forEach((bindingValue) => {
    const bindingRecord = asRecord(bindingValue)
    if (!bindingRecord) {
      return
    }

    const bodyId = readOptionalString(bindingRecord.bodyId)
    const topologyEntryKey = readOptionalString(bindingRecord.topologyEntryKey)
    const topologyRef = normalizeImportedTopologyRef(bindingRecord.topologyRef)
    const topologyFaceTriangleRanges = readImportedTopologyFaceTriangleRangeBindings(
      bindingRecord.visibleFaceTriangleRanges,
      bodyId,
    )

    readMeshTopologyBindingPrimitiveIndices(bindingRecord).forEach((primitiveIndex) => {
      if (bindingMap.has(primitiveIndex)) {
        return
      }
      bindingMap.set(primitiveIndex, {
        bodyId,
        topologyEntryKey,
        topologyRef,
        topologyFaceTriangleRanges,
      })
    })
  })

  return bindingMap
}

/**
 * 读取 body 层 BRep 绑定索引。
 *
 * 设计说明：
 * 1. 新版 `part + single` 导出中，`entities[].entityId` 通常是 shell ID，而 `bodies[].bodyId` 是 body ID；
 * 2. 这两个 ID 不一定相同，不能继续用 entityId 直接查 body，否则会错误回退到 entity 级 BRep；
 * 3. `bodies[].primitiveIndices` 才是 body 与当前 primitive 的权威关联，因此必须优先按 primitive 下标命中。
 *
 * @param stepRecord mesh 级 STEP 记录。
 * @returns body 绑定反查索引。
 */
function buildBodyBrepBindingLookup(
  stepRecord: Record<string, unknown> | null,
): ImportedBodyBrepBindingLookup {
  const byPrimitiveIndex = new Map<number, ImportedBodyBrepBinding>()
  const byBodyId = new Map<string, ImportedBodyBrepBinding>()
  const bodyValues = Array.isArray(stepRecord?.bodies) ? stepRecord.bodies : []
  const ordinalByKind = new Map<'solid' | 'shell', number>()
  // 明确 Solid/Shell 可按各类型序号恢复；Surface 等描述不足的混合集合保留原始顺序，延迟几何匹配。
  const deferBodyBrepMatch = bodyValues.some((value) => {
    const type = asRecord(value)?.bodyType
    const bodyType = typeof type === 'string' ? type.trim().toLowerCase() : ''
    return bodyType.length > 0 && bodyType !== 'solid' && bodyType !== 'shell'
  })

  bodyValues.forEach((bodyValue, bodyIndex) => {
    const bodyRecord = asRecord(bodyValue)
    if (!bodyRecord) {
      return
    }

    const bodyId = typeof bodyRecord.bodyId === 'string' ? bodyRecord.bodyId.trim() : ''
    const brepRef = normalizeImportedBrepRef(bodyRecord.brepRef)
    const topologyRef = normalizeImportedTopologyRef(bodyRecord.topologyRef)
    if (!bodyId) {
      return
    }
    const bodyType = typeof bodyRecord.bodyType === 'string' ? bodyRecord.bodyType.trim().toLowerCase() : ''
    const brepBodyKind = bodyType === 'solid' || bodyType === 'shell' ? bodyType : undefined
    const brepBodyOrdinal = !deferBodyBrepMatch && brepBodyKind ? ordinalByKind.get(brepBodyKind) ?? 0 : bodyIndex
    // 混合 Solid/Shell 必须分别编号，避免曲面序号落到实体集合或实体内部的壳上。
    if (brepBodyKind) ordinalByKind.set(brepBodyKind, brepBodyOrdinal + 1)

    const binding: ImportedBodyBrepBinding = {
      bodyId,
      brepRef,
      topologyRef,
      brepBodyOrdinal,
      brepBodyKind,
      deferBodyBrepMatch,
    }
    if (!byBodyId.has(bodyId)) {
      byBodyId.set(bodyId, binding)
    }

    readPrimitiveIndices(bodyRecord.primitiveIndices).forEach((primitiveIndex) => {
      if (!byPrimitiveIndex.has(primitiveIndex)) {
        byPrimitiveIndex.set(primitiveIndex, binding)
      }
    })
  })

  return {
    byPrimitiveIndex,
    byBodyId,
  }
}

/**
 * 按当前 primitive 的语义层级解析最贴近的 BRep 引用。
 *
 * 设计说明：
 * 1. `part + single` 会把多个 body 合并进同一个根 mesh，mesh 级 BRep 只能代表聚合结果；
 * 2. 当前交互与拓扑 hydration 的最小权威单元是 body，因此必须先取 body 级引用；
 * 3. body 缺失时再退到所属 entity，最后才退到 mesh，避免零件读取到整体 BRep 后和可见 body 对不上。
 *
 * @param input 三个语义层级的候选引用。
 * @returns 最终命中的引用与来源层级。
 */
function resolvePrimitiveBrepRefBySemanticLevel(input: {
  bodyLevelBrepRef?: ImportedBrepRef
  entityLevelBrepRef?: ImportedBrepRef
  meshLevelBrepRef?: ImportedBrepRef
}): { brepRef?: ImportedBrepRef; sourceLevel?: ImportedBrepRefSourceLevel } {
  if (input.bodyLevelBrepRef) {
    return {
      brepRef: input.bodyLevelBrepRef,
      sourceLevel: 'body',
    }
  }

  if (input.entityLevelBrepRef) {
    return {
      brepRef: input.entityLevelBrepRef,
      sourceLevel: 'entity',
    }
  }

  if (input.meshLevelBrepRef) {
    return {
      brepRef: input.meshLevelBrepRef,
      sourceLevel: 'mesh',
    }
  }

  return {}
}

/**
 * 按语义层级解析当前 primitive 的 topo sidecar 引用。
 *
 * @param input topo 引用候选。
 * @returns 最贴近当前 body 的交互 topo 引用。
 */
function resolvePrimitiveTopologyRefBySemanticLevel(input: {
  meshBindingTopologyRef?: ImportedTopologyRef
  meshBindingTopologyEntryKey?: string
  bodyLevelTopologyRef?: ImportedTopologyRef
  entityLevelTopologyRef?: ImportedTopologyRef
  meshLevelTopologyRef?: ImportedTopologyRef
}): ImportedTopologyRef | undefined {
  const semanticTopologyRef = input.bodyLevelTopologyRef
    ?? input.entityLevelTopologyRef
    ?? input.meshLevelTopologyRef
  if (semanticTopologyRef) {
    return overrideTopologyRefKey(
      semanticTopologyRef,
      input.meshBindingTopologyEntryKey ?? input.meshBindingTopologyRef?.key,
    )
  }

  return input.meshBindingTopologyRef
}

/**
 * 构建单个 mesh 的权威绑定计划。
 *
 * @param meshValue mesh 原始记录。
 * @returns mesh 绑定计划；无 primitives 时返回 `null`。
 */
export function buildImportedMeshBrepBindingPlan(
  meshValue: unknown,
): ImportedMeshBrepBindingPlan | null {
  const meshRecord = asRecord(meshValue)
  if (!meshRecord) {
    return null
  }

  const primitives = Array.isArray(meshRecord.primitives) ? meshRecord.primitives : []
  if (primitives.length === 0) {
    return null
  }

  const stepRecord = resolveMeshStepRecord(meshRecord)
  const meshLevelBrepRef = resolveMeshLevelBrepRefFromMeshRecord(meshRecord)
  const meshLevelTopologyRef = resolveMeshLevelTopologyRefFromMeshRecord(meshRecord)
  const entityBindingMap = buildEntityBindingMap(stepRecord)
  const bodyBindingLookup = buildBodyBrepBindingLookup(stepRecord)
  const meshTopologyBindingMap = buildMeshTopologyBindingMap(meshRecord)

  return {
    meshLevelBrepRef,
    primitivePlans: primitives.map((primitiveValue, primitiveIndex) => {
      const primitiveRecord = asRecord(primitiveValue)
      const primitiveExtras = cloneMetadata(primitiveRecord?.extras)
      const entityBinding = entityBindingMap.get(primitiveIndex)
      const meshTopologyBinding = meshTopologyBindingMap.get(primitiveIndex)
      const bodyBinding = bodyBindingLookup.byPrimitiveIndex.get(primitiveIndex)
        ?? (entityBinding?.bodyId ? bodyBindingLookup.byBodyId.get(entityBinding.bodyId) : undefined)
        ?? (meshTopologyBinding?.bodyId ? bodyBindingLookup.byBodyId.get(meshTopologyBinding.bodyId) : undefined)
      const { brepRef, sourceLevel } = resolvePrimitiveBrepRefBySemanticLevel({
        bodyLevelBrepRef: bodyBinding?.brepRef,
        entityLevelBrepRef: entityBinding?.brepRef,
        meshLevelBrepRef,
      })
      // 保留所有 Body 的共享来源，Worker 结合 topo 几何建立 Body→Shape 绑定。
      const topologyRef = resolvePrimitiveTopologyRefBySemanticLevel({
        meshBindingTopologyRef: meshTopologyBinding?.topologyRef,
        meshBindingTopologyEntryKey: meshTopologyBinding?.topologyEntryKey,
        bodyLevelTopologyRef: bodyBinding?.topologyRef,
        entityLevelTopologyRef: entityBinding?.topologyRef,
        meshLevelTopologyRef,
      })

      return {
        primitiveIndex,
        entityId: entityBinding?.entityId,
        bodyId: bodyBinding?.bodyId ?? meshTopologyBinding?.bodyId ?? entityBinding?.bodyId,
        brepRef,
        topologyRef,
        topologyFaceTriangleRanges: meshTopologyBinding?.topologyFaceTriangleRanges,
        sourceLevel,
        deferBodyBrepMatch: sourceLevel === 'mesh' ? bodyBinding?.deferBodyBrepMatch : undefined,
        brepBodyOrdinal: sourceLevel === 'mesh' && bodyBinding
          ? bodyBinding.brepBodyOrdinal
          : undefined,
        brepBodyKind: sourceLevel === 'mesh' ? bodyBinding?.brepBodyKind : undefined,
        primitiveExtras,
      }
    }),
  }
}

/**
 * 构建 nodeIndex 到 mesh 绑定计划的映射。
 *
 * @param parserJson GLTF parser JSON。
 * @returns nodeIndex 映射表。
 */
export function buildParserNodeBrepBindingPlanMap(
  parserJson: unknown,
): Map<number, ImportedMeshBrepBindingPlan> {
  const parserRecord = asRecord(parserJson)
  const nodeBindingPlanMap = new Map<number, ImportedMeshBrepBindingPlan>()
  const meshItems = Array.isArray(parserRecord?.meshes) ? parserRecord.meshes : []
  const nodeItems = Array.isArray(parserRecord?.nodes) ? parserRecord.nodes : []

  nodeItems.forEach((nodeValue, nodeIndex) => {
    const nodeRecord = asRecord(nodeValue)
    const meshIndex = typeof nodeRecord?.mesh === 'number' ? nodeRecord.mesh : NaN
    if (!Number.isInteger(meshIndex) || meshIndex < 0 || meshIndex >= meshItems.length) {
      return
    }

    const plan = buildImportedMeshBrepBindingPlan(meshItems[meshIndex])
    if (!plan) {
      return
    }

    nodeBindingPlanMap.set(nodeIndex, plan)
  })

  return nodeBindingPlanMap
}

/**
 * 将原始 GLB JSON 里的绑定计划写回到场景对象。
 *
 * 设计说明：
 * 1. 这一步只负责把协议层权威计划投影到 three.js 对象树，不做任何 body 级聚合；
 * 2. 计划会优先写在带 node association 的对象上，后续 child mesh 可通过父节点回退读取；
 * 3. 这样 inline part / inline assembly 局部几何在后续 clone 后仍能保留这份权威计划。
 *
 * @param gltf 已解析的 GLTF。
 */
export function applyGltfBrepBindingPlansToScene(gltf: GLTF): void {
  const parserJson = (gltf.parser as unknown as { json?: unknown }).json
  const associationMap = (gltf.parser as unknown as { associations?: unknown }).associations
  const associations = associationMap instanceof Map
    ? associationMap as Map<unknown, unknown>
    : null
  if (!associations) {
    return
  }

  const nodeBindingPlanMap = buildParserNodeBrepBindingPlanMap(parserJson)
  if (nodeBindingPlanMap.size === 0) {
    return
  }

  gltf.scene.traverse((object) => {
    delete object.userData[IMPORTED_MESH_BREP_BINDING_PLAN_KEY]
    const associationRecord = asRecord(associations.get(object))
    const nodeIndex = typeof associationRecord?.nodes === 'number' ? associationRecord.nodes : NaN
    if (!Number.isInteger(nodeIndex)) {
      return
    }

    const bindingPlan = nodeBindingPlanMap.get(nodeIndex)
    if (!bindingPlan) {
      return
    }

    object.userData[IMPORTED_MESH_BREP_BINDING_PLAN_KEY] = {
      meshLevelBrepRef: bindingPlan.meshLevelBrepRef ? { ...bindingPlan.meshLevelBrepRef } : undefined,
      primitivePlans: bindingPlan.primitivePlans.map((primitivePlan) => ({
        primitiveIndex: primitivePlan.primitiveIndex,
        entityId: primitivePlan.entityId,
        bodyId: primitivePlan.bodyId,
        brepRef: primitivePlan.brepRef ? { ...primitivePlan.brepRef } : undefined,
        topologyRef: primitivePlan.topologyRef ? { ...primitivePlan.topologyRef } : undefined,
        topologyFaceTriangleRanges: primitivePlan.topologyFaceTriangleRanges?.map((range) => ({
          faceIndex: range.faceIndex,
          faceRef: range.faceRef,
          subEntityId: range.subEntityId,
          triangleRange: [range.triangleRange[0], range.triangleRange[1]],
        })),
        sourceLevel: primitivePlan.sourceLevel,
        brepBodyOrdinal: primitivePlan.brepBodyOrdinal,
        brepBodyKind: primitivePlan.brepBodyKind,
        deferBodyBrepMatch: primitivePlan.deferBodyBrepMatch,
        primitiveExtras: primitivePlan.primitiveExtras ? { ...primitivePlan.primitiveExtras } : undefined,
      })),
    } satisfies ImportedMeshBrepBindingPlan
  })
}

/**
 * 读取对象上缓存的 mesh 级 BRep 绑定计划。
 *
 * @param object 目标对象。
 * @returns 绑定计划；不存在时返回 `null`。
 */
function readImportedMeshBrepBindingPlan(object: THREE.Object3D | null | undefined): ImportedMeshBrepBindingPlan | null {
  if (!object) {
    return null
  }
  return asRecord(object.userData?.[IMPORTED_MESH_BREP_BINDING_PLAN_KEY]) as unknown as ImportedMeshBrepBindingPlan | null
}

/**
 * 解析当前 mesh 在其直接父节点 mesh 子序列中的偏移。
 *
 * 设计说明：
 * 1. glTF 多 primitive mesh 在 three.js 中通常会被展开成“父 Group + 多个直接子 Mesh”；
 * 2. 即使权威 binding plan 最终挂在更高层 wrapper 上，primitive 顺序仍由 mesh 直接父节点下的 mesh 子序列决定；
 * 3. 因此这里始终按直接父节点统计偏移，避免被上层单子节点 wrapper 干扰。
 *
 * @param mesh 目标 mesh。
 * @returns primitive 偏移量。
 */
function resolvePrimitiveIndexOffsetFromDirectParent(mesh: THREE.Mesh): number {
  const parent = mesh.parent
  if (!parent) {
    return 0
  }

  let meshSiblingIndex = 0
  for (const sibling of parent.children) {
    if (sibling === mesh) {
      break
    }
    if (sibling instanceof THREE.Mesh) {
      meshSiblingIndex += 1
    }
  }

  return meshSiblingIndex
}

/**
 * 从对象自身或祖先链解析 mesh 绑定计划上下文。
 *
 * @param mesh 目标 mesh。
 * @returns 绑定计划与 primitive 偏移。
 */
export function resolveImportedMeshBrepBindingPlanContext(
  mesh: THREE.Mesh,
): { bindingPlan: ImportedMeshBrepBindingPlan | null; primitiveIndexOffset: number } {
  const ownPlan = readImportedMeshBrepBindingPlan(mesh)
  if (ownPlan) {
    return {
      bindingPlan: ownPlan,
      primitiveIndexOffset: 0,
    }
  }

  let ancestor = mesh.parent
  while (ancestor) {
    const ancestorPlan = readImportedMeshBrepBindingPlan(ancestor)
    if (ancestorPlan) {
      return {
        bindingPlan: ancestorPlan,
        primitiveIndexOffset: resolvePrimitiveIndexOffsetFromDirectParent(mesh),
      }
    }
    ancestor = ancestor.parent
  }

  return {
    bindingPlan: null,
    primitiveIndexOffset: 0,
  }
}

/**
 * 解析指定 primitive 的绑定计划。
 *
 * @param bindingPlanContext 绑定计划上下文。
 * @param primitiveIndex 当前 mesh 内的局部 primitive 下标。
 * @returns primitive 绑定计划。
 */
export function resolveImportedPrimitiveBrepBindingPlan(
  bindingPlanContext: { bindingPlan: ImportedMeshBrepBindingPlan | null; primitiveIndexOffset: number },
  primitiveIndex: number,
): ImportedPrimitiveBrepBindingPlan | undefined {
  const targetPrimitiveIndex = primitiveIndex + bindingPlanContext.primitiveIndexOffset
  return bindingPlanContext.bindingPlan?.primitivePlans.find(
    (primitivePlan) => primitivePlan.primitiveIndex === targetPrimitiveIndex,
  )
}
